// client/src/pushWeb.js
//
// Notificaciones push en el navegador, que es lo que convierte la web instalada
// en la pantalla de inicio de un iPhone en algo equivalente a la app de Android.
//
// Reutiliza la infraestructura que ya existe: el token que devuelve Firebase se
// registra contra /api/devices igual que hace la app Android, se guarda en la
// misma tabla device_tokens y lo envía el mismo pushSender.js. No hace falta ni
// una tabla nueva ni un servicio de push aparte.
//
// ── Lo que iOS exige, y no es negociable ─────────────────────────────────────
// Safari solo entrega notificaciones push a una web que el usuario haya añadido
// a la pantalla de inicio, desde iOS 16.4. Con la web abierta como una pestaña
// normal, getToken() falla o el permiso ni siquiera se puede pedir. Por eso
// isStandalone() se comprueba antes que nada: sin eso, el resto no tiene sentido.

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// El service worker no lo procesa webpack, así que la configuración viaja en la
// URL de registro. Son valores públicos —van en el bundle de todas formas—, no
// credenciales.
const SW_URL = `/firebase-messaging-sw.js?${new URLSearchParams({
  apiKey: firebaseConfig.apiKey || '',
  authDomain: firebaseConfig.authDomain || '',
  projectId: firebaseConfig.projectId || '',
  messagingSenderId: firebaseConfig.messagingSenderId || '',
  appId: firebaseConfig.appId || '',
}).toString()}`;

let messagingPromise = null;
let swRegistration = null;

// ── Diagnóstico del entorno ──────────────────────────────────────────────────

/** ¿Estamos dentro de la app instalada, y no en una pestaña del navegador? */
export function isStandalone() {
  try {
    // El primero es el estándar; navigator.standalone es el de Safari en iOS.
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

export function isIOS() {
  try {
    const ua = navigator.userAgent || '';
    // iPadOS se presenta como Mac, así que se detecta por el soporte táctil.
    return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  } catch {
    return false;
  }
}

/**
 * Por qué no se puede activar el push aquí y ahora, o null si sí se puede.
 * Sirve para explicarle al usuario qué le falta en vez de fallar en silencio.
 */
export function pushBlocker() {
  if (!firebaseConfig.projectId || !VAPID_KEY) return 'sin-configurar';
  if (!('serviceWorker' in navigator)) return 'navegador-sin-service-worker';
  if (!('Notification' in window) || !('PushManager' in window)) return 'navegador-sin-push';
  // La condición de iOS: en pestaña normal el push no existe.
  if (isIOS() && !isStandalone()) return 'ios-no-instalada';
  if (Notification.permission === 'denied') return 'permiso-denegado';
  return null;
}

// ── Arranque perezoso ────────────────────────────────────────────────────────

async function getMessagingInstance() {
  if (messagingPromise) return messagingPromise;

  messagingPromise = (async () => {
    if (!(await isSupported())) return null;
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(app);
  })().catch(() => null);

  return messagingPromise;
}

async function getRegistration() {
  if (swRegistration) return swRegistration;
  swRegistration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  // getToken() falla si el service worker todavía no está activo.
  await navigator.serviceWorker.ready;
  return swRegistration;
}

// ── Alta y baja del dispositivo ──────────────────────────────────────────────

/**
 * Pide permiso, obtiene el token de Firebase y registra el dispositivo.
 *
 * Tiene que llamarse desde un gesto del usuario (un botón), porque Safari ignora
 * Notification.requestPermission() si no viene de una interacción directa.
 *
 * @param {string} authToken el JWT de la sesión (el mismo del localStorage).
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function enablePush(authToken) {
  const blocker = pushBlocker();
  if (blocker) return { ok: false, reason: blocker };
  if (!authToken) return { ok: false, reason: 'sin-sesion' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'permiso-denegado' };

  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: 'navegador-sin-push' };

  try {
    const registration = await getRegistration();
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return { ok: false, reason: 'sin-token' };

    const response = await fetch('/api/devices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token, platform: isIOS() ? 'ios' : 'web' }),
    });
    if (!response.ok) return { ok: false, reason: 'servidor' };

    // Deja constancia para saber si hay que reintentar en el próximo arranque.
    try { localStorage.setItem('push_token', token); } catch { /* noop */ }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'error', detail: e && e.message };
  }
}

/**
 * Da de baja este dispositivo. Se llama al cerrar sesión: si no, el celular sigue
 * recibiendo los avisos de una cuenta que ya no está abierta en él.
 *
 * Necesita el token de sesión anterior, porque cuando esto corre el localStorage
 * ya está limpio.
 */
export async function disablePush(previousAuthToken) {
  let token = null;
  try { token = localStorage.getItem('push_token'); } catch { /* noop */ }
  try { localStorage.removeItem('push_token'); } catch { /* noop */ }
  if (!token || !previousAuthToken) return;

  // Primero el servidor: si borramos el token en Firebase antes, perdemos el dato
  // que hay que mandar para dar de baja la fila.
  try {
    await fetch('/api/devices', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${previousAuthToken}`,
      },
      body: JSON.stringify({ token }),
    });
  } catch { /* best-effort: el servidor purga los tokens muertos por su cuenta */ }

  try {
    const messaging = await getMessagingInstance();
    if (messaging) await deleteToken(messaging);
  } catch { /* noop */ }
}

/**
 * Vuelve a registrar el dispositivo si ya se había concedido el permiso.
 *
 * Firebase rota el token cada cierto tiempo, así que hay que reenviarlo en cada
 * arranque. Es idempotente: /api/devices hace upsert sobre el token.
 * Al no pedir permiso, no necesita gesto del usuario.
 */
export async function refreshPush(authToken) {
  if (!authToken) return { ok: false, reason: 'sin-sesion' };
  if (pushBlocker()) return { ok: false, reason: pushBlocker() };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'sin-permiso' };
  return enablePush(authToken);
}

/**
 * Avisos que llegan con la app abierta y en primer plano.
 *
 * En ese caso el navegador no pinta nada por su cuenta —da por hecho que el
 * usuario ya está mirando—, así que se lo pasamos al callback para que la web
 * decida: normalmente un toast dentro de la interfaz, no una notificación del
 * sistema.
 *
 * @returns {Promise<Function>} función para dejar de escuchar.
 */
export async function onPushInForeground(callback) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    const n = payload.notification || {};
    const d = payload.data || {};
    callback({
      title: n.title || d.title || '',
      body: n.body || d.body || '',
      tag: d.tag || '',
      link: d.link || '',
    });
  });
}
